import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'; // Import useNavigate
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext'; // Import useCart
import { db } from '../firebase/config';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore'; // Import addDoc and serverTimestamp
import { saveOrderToFirestore, updateOrderShiprocketDetails } from '../services/orderService';
import { createShiprocketOrder, getEstimatedDeliveryDate } from '../services/shippingService';
import { getApiBaseUrl } from '../services/apiConfig';
import './CheckoutPage.css';

const CheckoutPage = () => {
  // Razorpay credentials are now securely stored in Firebase Functions
  // No sensitive data exposed on client-side

  // Simple error state to help with debugging
  const [debugError, setDebugError] = useState(null);


  // Function to create secure Razorpay order via Firebase Functions
  const createSecureRazorpayOrder = async (amount, receipt) => {
    try {
      const response = await fetch(getApiBaseUrl('createRazorpayOrder'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          currency: 'INR',
          receipt: receipt
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        return data.order;
      } else {
        throw new Error(data.error || 'Failed to create payment order');
      }
    } catch (error) {
      console.error('Error creating secure Razorpay order:', error);
      throw error;
    }
  };

  // Function to load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { items, getCartTotal, getDiscountAmount, getFinalTotal, appliedCoupon, clearCart, addToCart, getItemTotalPrice } = useCart();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false); // New state for form visibility
  const [estimatedDate, setEstimatedDate] = useState(null);
  const [estimatingDelivery, setEstimatingDelivery] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('online');


  // State for new address form
  const [newAddress, setNewAddress] = useState({
    name: '',
    phone: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    landmark: '',
    pincode: '',
    city: '',
    state: '',
    country: 'India',
    addressType: 'Home',
    default: false,
  });
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);


  useEffect(() => {
    const fetchAddresses = async () => {
      if (!currentUser) {
        setLoading(false);
        setError("You are not logged in. Please log in or create an account to manage addresses.");
        setAddresses([]); // Clear addresses if not logged in
        return;
      }
      try {
        const q = query(collection(db, 'addresses'), where('userId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        const fetchedAddresses = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAddresses(fetchedAddresses);
        const defaultAddress = fetchedAddresses.find(addr => addr.default);
        if (defaultAddress) {
          setSelectedAddress(defaultAddress.id);
        } else if (fetchedAddresses.length > 0) {
          setSelectedAddress(fetchedAddresses[0].id);
        }
      } catch (err) {
        console.error("Error fetching addresses:", err);
        setError("Failed to fetch addresses. Please try again later.");
        setDebugError(`Address fetch error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [currentUser]);

  // Track Meta Pixel InitiateCheckout event
  useEffect(() => {
    if (items.length > 0 && window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        value: getFinalTotal(),
        currency: 'INR',
        content_ids: items.map(item => item.id),
        content_type: 'product',
        num_items: items.reduce((acc, item) => acc + item.quantity, 0)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchDeliveryDate = async () => {
      if (!selectedAddress || !addresses.length) return;
      const addr = addresses.find(a => a.id === selectedAddress);
      if (addr && addr.pincode) {
        setEstimatingDelivery(true);
        try {
          const res = await getEstimatedDeliveryDate(addr.pincode);
          if (res && res.success && res.estimated_delivery_date) {
            setEstimatedDate(res.estimated_delivery_date);
          } else {
            setEstimatedDate(null);
          }
        } catch (e) {
          console.error("Error formatting delivery date", e);
          setEstimatedDate(null);
        } finally {
          setEstimatingDelivery(false);
        }
      }
    };
    fetchDeliveryDate();
  }, [selectedAddress, addresses]);

  const handleAddressSelect = (addressId) => {
    setSelectedAddress(addressId);
  };

  const handleNewAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAddress(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddNewAddress = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert("You must be logged in to add a new address.");
      return;
    }
    if (!newAddress.name || !newAddress.phone || !newAddress.addressLine1 || !newAddress.pincode || !newAddress.city || !newAddress.state) {
      alert("Please fill in all required address fields (Name, Phone, Address Line 1, Pincode, City, State).");
      return;
    }

    try {
      setLoading(true);
      const addressData = {
        ...newAddress,
        userId: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // If new address is set as default, update existing default address
      if (newAddress.default) {
        const currentDefault = addresses.find(addr => addr.default);
        if (currentDefault) {
          // You would typically update the existing default address to false here
          // This requires another Firestore operation (updateDoc) which is not in current tools
          // For now, new default will just override logically in UI after fetch
          console.warn("Existing default address would need to be unset in a real scenario.");
        }
      }

      const docRef = await addDoc(collection(db, 'addresses'), addressData);
      setAddresses(prev => [...prev, { id: docRef.id, ...addressData }]);
      setSelectedAddress(docRef.id); // Select the newly added address
      setNewAddress({ // Reset form
        name: '', phone: '', email: '', addressLine1: '', addressLine2: '',
        landmark: '', pincode: '', city: '', state: '', country: 'India',
        addressType: 'Home', default: false,
      });
      setShowAddressForm(false); // Hide form
      alert("Address added successfully!");
    } catch (err) {
      console.error("Error adding new address:", err);
      setError("Failed to add new address. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const createShiprocketAndUpdateOrder = async (orderId, selectedAddressData, method) => {
    const now = new Date();
    const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    const orderData = {
      order_id: orderId,
      order_date: formattedDate,
      pickup_location: "shop",
      billing_customer_name: selectedAddressData.name,
      billing_last_name: "",
      billing_address: selectedAddressData.addressLine1,
      billing_address_2: selectedAddressData.addressLine2 || "",
      billing_city: selectedAddressData.city,
      billing_pincode: selectedAddressData.pincode,
      billing_state: selectedAddressData.state,
      billing_country: selectedAddressData.country || "India",
      billing_email: selectedAddressData.email || "",
      billing_phone: selectedAddressData.phone || "",
      shipping_is_billing: true,
      order_items: items.map(item => ({
        name: item.name,
        sku: item.id || "SKU",
        units: item.quantity,
        selling_price: item.price,
      })),
      payment_method: method === 'cod' ? "COD" : "Prepaid",
      shipping_charges: 0,
      giftwrap_charges: 0,
      transaction_charges: 0,
      total_discount: getDiscountAmount(),
      sub_total: getFinalTotal(),
      length: 40,
      breadth: 30,
      height: 8,
      weight: 2,
    };

    try {
      const shipResponse = await createShiprocketOrder(orderData);
      if (shipResponse && shipResponse.data && !shipResponse.data.order_id && shipResponse.data.status_code !== 1) {
        console.error('Shiprocket returned failure inside 200 payload:', shipResponse.data);
        alert('Shiprocket order failed (API error): ' + JSON.stringify(shipResponse.data));
      } else {
        console.log("Shiprocket Order Created!", shipResponse.data);
        await updateOrderShiprocketDetails(
          orderId,
          shipResponse.data.shipment_id,
          shipResponse.data.order_id
        );
        console.log("Updated Firestore with Shipment tracking IDs.");
      }
    } catch (shipError) {
      console.error('Shiprocket order creation failed:', shipError);
      alert('Shiprocket order failed: ' + (shipError.message || JSON.stringify(shipError)));
    }
  };

  const handleProceedToPayment = async () => {
    if (!currentUser && items.length > 0) {
      alert("Please log in to proceed with the payment.");
      navigate('/login', { state: { from: location } });
      return;
    }
    if (!selectedAddress && addresses.length > 0) {
      alert("Please select a shipping address.");
      return;
    }
    if (items.length === 0) {
      alert("Your cart is empty.");
      return;
    }

    setLoading(true);
    const receiptId = `NEXA-ORD-${Date.now()}`;
    const totalAmount = getFinalTotal();

    try {
      // Get selected address details
      let selectedAddressData = selectedAddress 
        ? addresses.find(addr => addr.id === selectedAddress)
        : null;

      if (!selectedAddressData && newAddress && newAddress.name) {
        selectedAddressData = newAddress;
      }

      if (!selectedAddressData || !selectedAddressData.name || !selectedAddressData.addressLine1) {
        alert("Please save or select a complete shipping address.");
        setLoading(false);
        return;
      }

      if (paymentMethod === 'cod') {
        try {
          const orderResult = await saveOrderToFirestore(
            {},
            currentUser ? currentUser.uid : null,
            items,
            selectedAddressData,
            appliedCoupon,
            { paymentMethod: 'COD', paymentStatus: 'PENDING' }
          );

          if (orderResult.success) {
            await createShiprocketAndUpdateOrder(orderResult.orderId, selectedAddressData, 'cod');
            
            // Track Meta Pixel Purchase event (COD)
            if (window.fbq) {
              window.fbq('track', 'Purchase', {
                value: totalAmount,
                currency: 'INR',
                content_ids: items.map(item => item.id),
                content_type: 'product',
                contents: items.map(item => ({
                  id: item.id,
                  quantity: item.quantity,
                  item_price: item.price
                })),
                payment_method: 'COD'
              });
            }

            navigate(`/order-confirmation/${orderResult.orderId}`);
          } else {
            throw new Error('Failed to save order to Firestore');
          }
        } catch (err) {
          console.error('Error processing COD order:', err);
          alert('Failed to process COD order: ' + err.message);
        }
        setLoading(false);
        return;
      }

      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded) {
        alert('Failed to load payment gateway. Please try again.');
        setLoading(false);
        return;
      }

      const razorpayOrder = await createSecureRazorpayOrder(totalAmount, receiptId);

      const options = {
        key: razorpayOrder.key_id,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        order_id: razorpayOrder.id,
        name: 'NexaWave',
        description: 'Order payment',
        prefill: {
          name: selectedAddressData.name,
          email: selectedAddressData.email || '',
          contact: selectedAddressData.phone || ''
        },
        handler: async (response) => {
          setLoading(true);
          try {
            const verifyResponse = await fetch(getApiBaseUrl('verifyRazorpayPayment'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(response)
            });

            const verifyResult = await verifyResponse.json();
            if (!verifyResponse.ok || !verifyResult.success) {
              throw new Error(verifyResult.error || 'Payment verification failed');
            }

            const orderResult = await saveOrderToFirestore(
              response,
              currentUser ? currentUser.uid : null,
              items,
              selectedAddressData,
              appliedCoupon,
              { paymentMethod: 'RAZORPAY', paymentStatus: 'COMPLETED' }
            );

            if (orderResult.success) {
              await createShiprocketAndUpdateOrder(orderResult.orderId, selectedAddressData, 'online');
              
              // Track Meta Pixel Purchase event (Online)
              if (window.fbq) {
                window.fbq('track', 'Purchase', {
                  value: totalAmount,
                  currency: 'INR',
                  content_ids: items.map(item => item.id),
                  content_type: 'product',
                  contents: items.map(item => ({
                    id: item.id,
                    quantity: item.quantity,
                    item_price: item.price
                  })),
                  payment_method: 'RAZORPAY'
                });
              }

              navigate(`/order-confirmation/${orderResult.orderId}`);
            } else {
              throw new Error('Failed to save order to Firestore');
            }
          } catch (err) {
            console.error('Error processing payment:', err);
            alert('Payment succeeded but order processing failed: ' + err.message);
          } finally {
            setLoading(false);
          }
        },
        theme: {
          color: '#0156FF'
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on('payment.failed', (response) => {
        console.error('Razorpay payment failed:', response.error);
        alert('Payment failed. Please try again.');
      });

      setLoading(false);
      razorpayInstance.open();
    } catch (error) {
      console.error('Error initiating payment:', error);
      alert('Failed to initiate payment: ' + error.message);
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    // Handle different price formats and ensure we have a valid number
    let numPrice = price;
    
    if (typeof price === 'string') {
      // Remove any currency symbols and convert to number
      numPrice = parseFloat(price.replace(/[₹,\s]/g, ''));
    }
    
    if (!numPrice || isNaN(numPrice) || numPrice <= 0) {
      return '₹0';
    }
    
    return `₹${Number(numPrice).toLocaleString('en-IN')}`;
  };

  // Adjust loading/error messages for guest users
  if (loading && currentUser) {
    return (
      <div className="checkout-page">
        <div className="checkout-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error && currentUser && addresses.length === 0) {
    return (
      <div className="checkout-page error">
        <div className="checkout-container">
          <h2>Error Loading Checkout</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      {/* Debug Error Display */}
      {debugError && (
        <div className="checkout-container">
          <div className="debug-alert">
            <strong>Debug Info:</strong> {debugError}
            <br />
            <small>Please take a screenshot of this and check browser console (F12)</small>
          </div>
        </div>
      )}

      <div className="checkout-container">
        <div className="checkout-breadcrumbs">
          <span>Home</span>
          <span className="separator">›</span>
          <span>Shopping Cart</span>
          <span className="separator">›</span>
          <span className="active">Checkout Process</span>
        </div>
        
        <h1 className="checkout-main-title">Checkout</h1>

        <div className="checkout-grid">
          {/* Left Column - Shipping Address */}
          <div className="checkout-left">
            {!currentUser && (
              <div className="guest-login-section">
                <button className="sign-in-btn" onClick={() => navigate('/login', { state: { from: location } })}>
                  <span>Sign In</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
            <div className="section-body">
              {currentUser && addresses.length === 0 && !showAddressForm ? (
                <p className="no-address-msg">
                  No addresses found. Please add a new address below.
                </p>
              ) : null}

              {currentUser && addresses.length > 0 && !showAddressForm && (
                <div className="address-list">
                  {addresses.map((address) => (
                    <div
                      key={address.id}
                      className={`address-card ${selectedAddress === address.id ? 'selected' : ''}`}
                      onClick={() => handleAddressSelect(address.id)}
                    >
                      <input
                        type="radio"
                        id={address.id}
                        name="shippingAddress"
                        value={address.id}
                        checked={selectedAddress === address.id}
                        onChange={() => handleAddressSelect(address.id)}
                        className="address-radio"
                      />
                      <div className="address-content">
                        <div className="address-name">
                          <strong>{address.name}</strong>
                          {address.addressType && (
                            <span className="address-type-badge">{address.addressType}</span>
                          )}
                        </div>
                        <div className="address-details">
                          {address.addressLine1}
                          {address.addressLine2 && <><br />{address.addressLine2}</>}
                          <br />
                          {address.landmark && `${address.landmark}, `}
                          {address.city}, {address.pincode}
                          <br />
                          {address.state}, {address.country}
                        </div>
                        <div className="address-contact">
                          {address.phone && <div>Ph: {address.phone}</div>}
                          {address.email && <div>Email: {address.email}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {showAddressForm ? (
                <form onSubmit={handleAddNewAddress} className="add-address-form">
                  <h3 style={{marginBottom: '20px', fontSize: '18px', fontWeight: '600'}}>Add New Address</h3>
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <input 
                        type="text" 
                        name="name" 
                        placeholder="Full Name" 
                        value={newAddress.name} 
                        onChange={handleNewAddressChange} 
                        required 
                        className="form-input"
                      />
                    </div>
                    <div className="form-group full-width">
                      <input 
                        type="tel" 
                        name="phone" 
                        placeholder="Mobile Number" 
                        value={newAddress.phone} 
                        onChange={handleNewAddressChange} 
                        required 
                        className="form-input"
                      />
                    </div>
                    <div className="form-group full-width">
                      <input 
                        type="email" 
                        name="email" 
                        placeholder="Email (Optional)" 
                        value={newAddress.email} 
                        onChange={handleNewAddressChange} 
                        className="form-input"
                      />
                      <p className="form-help-text">You can create an account after checkout.</p>
                    </div>
                    <div className="form-group full-width">
                      <input 
                        type="text" 
                        name="addressLine1" 
                        placeholder="Address Line 1 (House No., Building, Street)" 
                        value={newAddress.addressLine1} 
                        onChange={handleNewAddressChange} 
                        required 
                        className="form-input"
                      />
                    </div>
                    <div className="form-group full-width">
                      <input 
                        type="text" 
                        name="addressLine2" 
                        placeholder="Address Line 2 (Area, Colony, Road)" 
                        value={newAddress.addressLine2} 
                        onChange={handleNewAddressChange} 
                        className="form-input"
                      />
                    </div>
                    <div className="form-group full-width">
                      <input 
                        type="text" 
                        name="landmark" 
                        placeholder="Landmark (Optional)" 
                        value={newAddress.landmark} 
                        onChange={handleNewAddressChange} 
                        className="form-input"
                      />
                    </div>
                    <div className="form-group full-width">
                      <input 
                        type="text" 
                        name="pincode" 
                        placeholder="Pincode" 
                        value={newAddress.pincode} 
                        onChange={handleNewAddressChange} 
                        required 
                        className="form-input"
                      />
                    </div>
                    <div className="form-group full-width">
                      <input 
                        type="text" 
                        name="city" 
                        placeholder="City" 
                        value={newAddress.city} 
                        onChange={handleNewAddressChange} 
                        required 
                        className="form-input"
                      />
                    </div>
                    <div className="form-group full-width">
                      <input 
                        type="text" 
                        name="state" 
                        placeholder="State" 
                        value={newAddress.state} 
                        onChange={handleNewAddressChange} 
                        required 
                        className="form-input"
                      />
                    </div>
                    <div className="form-group full-width">
                      <select 
                        name="addressType" 
                        value={newAddress.addressType} 
                        onChange={handleNewAddressChange}
                        className="form-select"
                      >
                        <option value="Home">Home</option>
                        <option value="Work">Work</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group full-width">
                      <div className="checkbox-wrapper" style={{display: 'flex', alignItems: 'center'}}>
                        <label className="checkbox-input" style={{marginRight: '10px'}}>
                          <input 
                            type="checkbox" 
                            name="default" 
                            checked={newAddress.default} 
                            onChange={handleNewAddressChange} 
                            style={{width: '18px', height: '18px'}}
                          />
                        </label>
                        <span className="checkbox-label" style={{fontSize: '14px', color: '#000'}}>Set as default address</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">Save Address</button>
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => setShowAddressForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                currentUser && ( 
                  <button
                    className="add-new-address-button"
                    onClick={() => setShowAddressForm(true)}
                  >
                    + Add New Address
                  </button>
                )
              )}
              
              {!currentUser && (
                <div className="guest-checkout-info">
                  <p>
                    You are checking out as a guest. You will be prompted for your address at the next step.
                    <br />
                    Please <Link to="/login" state={{ from: location }}>log in</Link> or <Link to="/signup" state={{ from: location }}>sign up</Link> to save and manage your addresses.
                  </p>
                </div>
              )}
            </div>

            {/* Standard Rate / Pickup Section - Mocking visual from Figma as per requirements, functional payment proceed stays */}
            <div className="shipping-methods">
              <h3 className="section-title" style={{marginTop: '30px', fontSize: '18px'}}>Shipping Method</h3>
              <div className="shipping-method-row">
                <input type="radio" name="shipping-rate" id="standard-rate" defaultChecked />
                <label htmlFor="standard-rate" className="rate-info">
                  <span>{estimatingDelivery ? "Calculating delivery date..." : estimatedDate ? `Get it couriered by ${new Date(estimatedDate).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })} to address` : "Get it couriered by 3 days to address"}</span>
                </label>
              </div>
              <div className="shipping-method-row">
                <input type="radio" name="shipping-rate" id="pickup-store" />
                <label htmlFor="pickup-store" className="rate-info">
                  <span>Pickup from store H.No. 16-5-1/4, Farhat Nagar Hyderabad Hyderabad TELANGANA 500023</span>
                </label>
              </div>
            </div>

            <div className="payment-methods">
              <h3 className="section-title" style={{marginTop: '30px', fontSize: '18px'}}>Payment Method</h3>
              <div className="payment-method-row">
                <input
                  type="radio"
                  name="payment-method"
                  id="payment-online"
                  value="online"
                  checked={paymentMethod === 'online'}
                  onChange={() => setPaymentMethod('online')}
                />
                <label htmlFor="payment-online" className="rate-info">
                  <span>Online Payment (UPI, Card, NetBanking) - Ships within 12 hrs</span>
                </label>
              </div>
              <div className="payment-method-row">
                <input
                  type="radio"
                  name="payment-method"
                  id="payment-cod"
                  value="cod"
                  checked={paymentMethod === 'cod'}
                  onChange={() => setPaymentMethod('cod')}
                />
                <label htmlFor="payment-cod" className="rate-info">
                  <span>Cash on Delivery</span>
                </label>
              </div>
            </div>

            <button
                className="proceed-to-payment-button"
                onClick={handleProceedToPayment}
                disabled={items.length === 0 || (currentUser && addresses.length === 0 && !showAddressForm) || (currentUser && addresses.length > 0 && !selectedAddress)}
              >
                Proceed to Pay
            </button>
          </div>

          {/* Right Column - Order Summary */}
          <div className="checkout-right">
            <div className="checkout-steps">
              <div className="step active">
                <div className="step-icon"><svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5L5 9L13 1" stroke="#0156FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
                <span>Shipping</span>
              </div>
              <div className="step-line"></div>
              <div className="step">
                <div className="step-icon">2</div>
                <span>Review & Payments</span>
              </div>
            </div>

            <div className="order-summary-box">
              <h2 className="summary-title">Order Summary</h2>
              
              <div className="order-summary-content">
                <div className="summary-expand-header" onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}>
                  <span>{items.length} {items.length === 1 ? 'Item' : 'Items'} in Cart</span>
                  <span className={`expand-icon ${isSummaryExpanded ? 'open' : ''}`}>
                    {isSummaryExpanded ? '▲' : '▼'}
                  </span>
                </div>
                
                {isSummaryExpanded && (
                  <div className="summary-body">
                    {items.length === 0 ? (
                      <p className="empty-cart-msg">Your cart is empty.</p>
                    ) : (
                      <>
                        {items.map((item, index) => (
                          <div key={index} className="order-summary-item">
                            <img 
                              src={item.imageUrls?.[0] || 'https://via.placeholder.com/80'} 
                              alt={item.name} 
                              className="item-image" 
                            />
                            <div className="item-details">
                              <h4 className="item-title">{item.name}</h4>
                              {(item.selectedRam || item.selectedStorage) && (
                                <div className="item-options">
                                  {item.selectedRam && <span>RAM: {item.selectedRam}</span>}
                                  {item.selectedRam && item.selectedStorage && <span> | </span>}
                                  {item.selectedStorage && <span>Storage: {item.selectedStorage}</span>}
                                </div>
                              )}
                              <div className="item-meta">
                                <span className="item-qty">Qty <strong>{item.quantity}</strong></span>
                                <span className="item-price">{formatPrice(getItemTotalPrice(item) * item.quantity)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        <div className="order-breakdown">
                          <div className="breakdown-row total">
                            <span className="breakdown-label">Total Amount</span>
                            <span className="breakdown-value">{formatPrice(getFinalTotal())}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
